import React from "react";
import { toast } from "react-toastify";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgCopyTextTypography,
  IgPaper,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { OrganizationSubscriptionDto } from "@intelligems/ig-types";
import { removeTrailingSlash } from "@intelligems/ig-utils";
import { Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { theme } from "@src/theme";

import {
  useGetOrg,
  useGetOrgId,
} from "../../api/organizations/get-organization";
import { usePatchOrganization } from "../../api/organizations/patch-organization";
import { useManualDeploy } from "../../api/plugin-configs/manual-deploy";
import { useIsAdmin } from "../../api/user-auths/is-admin";
import { urlRegex } from "../../utils/constants";

const PREFIX = "OrganizationInformation";

const classes = {
  dataLabel: `${PREFIX}-dataLabel`,
  sectionLabel: `${PREFIX}-sectionLabel`,
};

const StyledPaper = styled(IgPaper)(({ theme }) => ({
  [`& .${classes.dataLabel}`]: {
    fontWeight: "bold",
    marginRight: 20,
  },
  [`& .${classes.sectionLabel}`]: {
    marginTop: 5,
  },
}));

export const OrganizationInformation = () => {
  const isAdmin = useIsAdmin();
  const manualDeploy = useManualDeploy();
  const [error, setError] = useImmer<boolean>(false);
  const organizationId = useGetOrgId();
  const organization = useGetOrg({
    refetchOnWindowFocus: "always",
  });
  const [url, setUrl] = useImmer<string>(organization.data.org.domain || "");
  const patchOrganization = usePatchOrganization();

  const handleChange = (value: string) => {
    setUrl(value);
    if (value.length <= 8 || urlRegex.test(value)) {
      setError(false);
    } else {
      setError(true);
    }
  };

  const handleYesClick = () => {
    patchOrganization.mutate({
      body: {
        domain: removeTrailingSlash(url),
      },
      queryParams: undefined,
      pathParams: undefined,
    });
  };

  return (
    <StyledPaper sx={{ height: "220px", boxShadow: "none" }}>
      <Grid direction={"column"} container rowSpacing={2}>
        <Grid
          container
          item
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid item>
            <IgTypography
              fontWeight={"fontWeightMedium"}
              variant="h3"
              className={classes.sectionLabel}
            >
              Organization Information
            </IgTypography>
          </Grid>
          {isAdmin ? (
            <Grid item>
              <IgButton
                size={"small"}
                loading={manualDeploy.isPending}
                variant={"contained"}
                onClick={() => {
                  manualDeploy.mutate();
                }}
              >
                Deploy
              </IgButton>
            </Grid>
          ) : null}
        </Grid>
        <Grid container direction={"row"} item xs={12}>
          <IgTypography className={classes.dataLabel}>Name:</IgTypography>
          <IgTypography>{organization.data.org.name}</IgTypography>
        </Grid>
        <Grid
          container
          direction={"row"}
          item
          flexWrap={"nowrap"}
          alignItems={"center"}
          columnSpacing={1}
        >
          <Grid item>
            <IgTypography className={classes.dataLabel}>Domain:</IgTypography>
          </Grid>
          <Grid container item direction={"row"}>
            <Grid container item columnSpacing={2}>
              <Grid item flexGrow={10}>
                <IgTextField
                  fullWidth={true}
                  value={url}
                  onChange={(e) => handleChange(e.target.value)}
                />
              </Grid>
              <Grid item>
                <IgButton
                  onClick={handleYesClick}
                  loading={patchOrganization.isPending}
                  disabled={error}
                  variant={"text"}
                >
                  Save
                </IgButton>
              </Grid>
            </Grid>
            <Grid item>
              {error ? (
                <IgTypography
                  sx={{ color: theme.palette.text.tonal.error }}
                  variant={"body2"}
                >
                  Invalid URL format
                </IgTypography>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
        <Grid container direction={"row"} item xs={12}>
          <IgTypography className={classes.dataLabel}>ID:</IgTypography>
          <IgTypography>
            <IgCopyTextTypography
              text={organization.data.org.id}
              toastMessage={(text) =>
                toast.success(
                  `Organization ${text.length === 12 ? "Short" : ""} ID Copied`
                )
              }
            />{" "}
            (
            <IgCopyTextTypography
              text={organizationId}
              toastMessage={(text) =>
                toast.success(
                  `Organization ${text.length === 12 ? "Short" : ""} ID Copied`
                )
              }
            />
            )
          </IgTypography>
        </Grid>
        <Grid container direction={"row"} item xs={12}>
          <IgTypography className={classes.dataLabel}>Shop ID:</IgTypography>
          <IgTypography>
            <IgCopyTextTypography
              text={organization.data.org.shopId ?? ""}
              toastMessage={(text) =>
                toast.success(`Organization Shop ID Copied`)
              }
            />
          </IgTypography>
        </Grid>
      </Grid>
    </StyledPaper>
  );
};
